import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { FullViewSpinner } from '@provider-portal/common/components/FullViewSpinner/FullViewSpinner';
import { useOnNavigateIn } from '@provider-portal/common/hooks/useOnNavigateIn';
import { AlertIcon } from '@provider-portal/common/icons/AlertIcon';
import { SuccessIcon } from '@provider-portal/common/icons/SuccessIcon';
import * as qs from 'query-string';

import { Button, Typography } from '@bolteu/clay-react';

import { useAppServices } from '../../components/AppServicesProvider';

import { $tk } from 'utils/appTranslationKey';
import { useLocalizedDocumentTitle } from 'root/navigation/useLocalizedDocumentTitle';

interface EmailConfirmationMessageProps {
  isSuccess: boolean;
  isError: boolean;
  onClick: () => void;
}

const queryStringObject = qs.parse(window.location.search);
const unlockAccountToken = queryStringObject?.token?.toString();

const AccountUnlockMessage: React.FC<EmailConfirmationMessageProps> = ({
  isSuccess,
  isError,
  onClick,
}) => {
  const { formatMessage } = useIntl();

  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  const confirmStatus = useMemo(() => {
    if (isSuccess) {
      return (
        <div>
          <div>
            <SuccessIcon className="size-28" />
          </div>
          <div className="mt-4">
            <Typography defaultTag="h5" className="bolt-font-heading-s-accent text-center">
              {formatMessage({ id: $tk.screens.AccountUnlock.unlockSuccess })}
            </Typography>
          </div>
          <div className="mt-20 flex justify-center">
            <Button size="md" onClick={handleClick}>
              {formatMessage({ id: $tk.screens.AccountUnlock.goToLogin })}
            </Button>
          </div>
        </div>
      );
    }
    if (isError) {
      return (
        <div>
          <div>
            <AlertIcon className="size-28" />
          </div>
          <div className="mt-4">
            <Typography defaultTag="h5" className="bolt-font-heading-s-accent text-center">
              {formatMessage({ id: $tk.screens.AccountUnlock.unlockFail })}
            </Typography>
          </div>
          <div className="mt-20 flex justify-center">
            <Button size="md" onClick={handleClick}>
              {formatMessage({ id: $tk.screens.AccountUnlock.goToLogin })}
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }, [formatMessage, handleClick, isError, isSuccess]);

  return <div className="flex h-full items-center justify-center text-center">{confirmStatus}</div>;
};

export const AccountUnlock = () => {
  useLocalizedDocumentTitle($tk.screens.AccountUnlock.title);

  const [isLoaderVisible, setLoaderVisible] = useState<boolean>(true);
  const [isSuccess, setSuccess] = useState<boolean>(false);
  const [isError, setError] = useState<boolean>(false);
  const history = useHistory();
  const { apiClient } = useAppServices();

  const handleOnClick = useCallback(() => {
    history.push('/');
  }, [history]);

  const sendUnlockAccountRequest = useCallback(async () => {
    try {
      await apiClient.deliveryProviderNoAuth.publicUnlockAccount({
        token: unlockAccountToken,
      });
      setSuccess(true);
      setError(false);
    } catch (_error) {
      setSuccess(false);
      setError(true);
    }
    setLoaderVisible(false);
  }, [apiClient.deliveryProviderNoAuth]);

  useOnNavigateIn(sendUnlockAccountRequest);

  return (
    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <div className="w-[85vw] max-w-[450px]">
        {isLoaderVisible ? (
          <FullViewSpinner />
        ) : (
          <AccountUnlockMessage isError={isError} isSuccess={isSuccess} onClick={handleOnClick} />
        )}
      </div>
    </div>
  );
};
